import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { lettersGo, mailboxSearchUrl, proposalWhat } from "@joogoos/contracts";
import type { ComingUpView, ProposalView, ToApproveView, TodayResponse } from "@joogoos/contracts";
import { api } from "../api.js";
import { Icon } from "../ui/icons.js";
import { FindPanel, priceWords } from "./FindPanel.js";
import { CompanyPanel } from "./CompanyPanel.js";

/**
 * **Customers → Today** (23 §the screens).
 *
 * The front door, and the argument for the whole tab. It was six screens that were three views of one table, and
 * it opened on Find — the most expensive and most optional thing a person can do here. Now it opens on *who is
 * waiting for you*, in that order:
 *
 * 1. **Somebody answered.** The only thing on this page that is a person's job rather than the software's.
 * 2. **Letters to approve**, each showing its one written sentence, so four can be judged without opening four.
 * 3. **A decision** the loop cannot make: a proposal, or a cell that stopped itself.
 * 4. **The loop**, reporting on itself in one line.
 * 5. What is going out, and what this has cost.
 *
 * On a Monday that is: answer two, send four, say yes to one. Five minutes, and no tab-switching.
 */

export const TODAY = ["customers", "today"];

export function Today() {
  const q = useQuery({ queryKey: TODAY, queryFn: () => api.today() });
  // The same answer the Find panel prices its button from, so the two figures cannot disagree.
  const sources = useQuery({ queryKey: ["customers", "sources"], queryFn: () => api.customerSources() });
  const searchPrice = sources.data?.sources.find((s) => s.code === "ask_ai")?.price ?? null;
  const client = useQueryClient();
  const [open, setOpen] = useState<string | null>(null);
  const [finding, setFinding] = useState(false);

  const answer = useMutation({
    mutationFn: ({ id, outcome }: { id: string; outcome: string }) => api.prospectOutcome(id, outcome),
    onSuccess: () => client.invalidateQueries({ queryKey: TODAY }),
  });
  const decide = useMutation({
    mutationFn: ({ id, decision }: { id: number; decision: "apply" | "reject" }) => api.decideProposal(id, decision),
    onSuccess: () => client.invalidateQueries({ queryKey: TODAY }),
  });
  const resume = useMutation({
    mutationFn: (id: number) => api.resumeCell(id),
    onSuccess: () => client.invalidateQueries({ queryKey: TODAY }),
  });
  const loop = useMutation({
    mutationFn: (running: boolean) => api.saveAutopilot({ running }),
    onSuccess: () => client.invalidateQueries({ queryKey: TODAY }),
  });
  const stop = useMutation({
    mutationFn: (id: string) => api.stopProspect(id, "stopped by hand"),
    onSuccess: () => client.invalidateQueries({ queryKey: TODAY }),
  });

  const d = q.data;
  if (q.isPending) return <p className="text-sm text-faint">…</p>;
  if (!d) return <p className="text-sm text-faint">Nothing here yet.</p>;

  return (
    <div className="flex flex-col gap-6 max-w-[1020px]">
      {d.missing.length > 0 && (
        <div className="rounded-xl border border-accent/30 bg-accent/5 p-3.5 flex flex-col gap-1">
          <p className="text-[12.5px] font-semibold">Before any of this can happen, it needs:</p>
          <ul className="text-[12.5px] text-muted list-disc pl-4">{d.missing.map((m) => <li key={m}>{m}</li>)}</ul>
        </div>
      )}

      {/* 1. Somebody answered. */}
      <section className="flex flex-col gap-2.5">
        <div className="flex flex-wrap items-center gap-2.5">
          <h1 className="text-base font-semibold">Your move</h1>
          {d.replies.length > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-accent/10 text-accent text-[11px] font-semibold tabular-nums">{d.replies.length}</span>
          )}
          <span className="text-[12px] text-muted">answers come first, and a person always answers them</span>
        </div>
        {d.replies.length === 0
          ? <p className="text-[12.5px] text-faint">Nobody is waiting on you.</p>
          : (
            <div className="grid gap-3 md:grid-cols-2">
              {d.replies.map((r) => (
                <div key={r.prospectId} className="rounded-xl border border-accent/30 bg-panel p-3.5 flex flex-col gap-2.5">
                  <div className="flex items-center gap-2">
                    <span className="px-1.5 py-0.5 rounded bg-accent/10 text-accent text-[10px] font-semibold uppercase tracking-[.05em]">replied</span>
                    <span className="text-[13px] font-semibold truncate">{r.companyName}</span>
                    <span className="text-[11px] text-faint ml-auto shrink-0">{when(r.at)}</span>
                  </div>
                  {/* True whichever way the answer was noticed — read, sent back to the reply address and forwarded, or
                      marked with They replied — so it says only what holds for all three (23 §they replied). */}
                  <p className="text-[12px] text-muted leading-relaxed">
                    Nothing more goes to them. Their answer is in your own mailbox, and answering it there answers them.
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {([["meeting", "A call is booked"], ["later", "Not now"], ["declined", "Said no"]] as const).map(([outcome, label], i) => (
                      <button
                        key={outcome} disabled={answer.isPending}
                        onClick={() => answer.mutate({ id: r.prospectId, outcome })}
                        className={`px-2.5 py-1 rounded-lg text-[11.5px] disabled:opacity-50 ${i === 0 ? "bg-ink text-white font-semibold" : "border border-line-2 bg-page font-medium"}`}
                      >{label}</button>
                    ))}
                    {r.toEmail && d.mailboxProvider && (
                      <a
                        href={mailboxSearchUrl(d.mailboxProvider, r.toEmail)}
                        target="_blank" rel="noreferrer"
                        className="px-2.5 py-1 rounded-lg border border-line-2 bg-page text-[11.5px] font-medium"
                      >Open in your mailbox</a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
      </section>

      {/* 2. Letters to approve — the daily act once the loop is running. */}
      <section className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2.5">
          <h2 className="text-[13px] font-semibold">Letters to approve</h2>
          {d.toApprove.length > 0 && <span className="text-[11px] text-muted tabular-nums">{d.toApprove.length}</span>}
          <span className="text-[11.5px] text-muted">best fit first · nothing goes until you press send</span>
        </div>
        {d.toApprove.length === 0
          ? (
            <p className="text-[12.5px] text-faint">
              Nothing is written yet. {d.loop.running ? "The loop writes them as it finds companies worth writing to." : "Start the loop, or find some companies yourself."}
            </p>
          )
          : (
            <div className="rounded-xl border border-line bg-panel overflow-hidden">
              {d.toApprove.map((l) => <Waiting key={l.letterId} letter={l} onOpen={() => setOpen(l.prospectId)} />)}
            </div>
          )}
      </section>

      {/* 3. What only a person can decide. Absent on most days, and absent from the screen when it is. */}
      {(d.proposals.length > 0 || d.pauses.length > 0) && (
        <section className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2.5">
            <h2 className="text-[13px] font-semibold">Waiting for you</h2>
            <span className="text-[11.5px] text-muted">read last night · at most four a night</span>
          </div>
          <div className="flex flex-col gap-2.5">
            {d.proposals.map((p) => (
              <div key={p.id} className="rounded-xl border border-line bg-panel p-3.5 flex flex-col gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="px-1.5 py-0.5 rounded bg-line-2 text-ink-2 text-[10px] font-semibold uppercase tracking-[.05em]">{KINDS[p.kind]}</span>
                  <span className="text-[13px] font-semibold">{headline(p)}</span>
                </div>
                <p className="text-[12.5px] text-muted leading-relaxed">{p.why}</p>
                <p className="text-[12.5px] leading-relaxed">{proposalWhat(p)}</p>
                <div className="flex gap-1.5">
                  <button
                    onClick={() => decide.mutate({ id: p.id, decision: "apply" })} disabled={!d.mayEdit || decide.isPending}
                    className="px-2.5 py-1 rounded-lg bg-ink text-white text-[11.5px] font-semibold disabled:opacity-50"
                  >Yes</button>
                  <button
                    onClick={() => decide.mutate({ id: p.id, decision: "reject" })} disabled={!d.mayEdit || decide.isPending}
                    className="px-2.5 py-1 rounded-lg border border-line-2 bg-page text-[11.5px] font-medium disabled:opacity-50"
                  >No</button>
                </div>
                {/* A yes that could not be done leaves the proposal here, so say why rather than seem to do nothing. */}
                {decide.error && decide.variables?.id === p.id ? <p className="text-xs text-bad">{String(decide.error)}</p> : null}
              </div>
            ))}
            {d.pauses.map((p) => (
              <div key={p.id} className="rounded-xl border border-accent/30 bg-panel p-3.5 flex flex-col gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="px-1.5 py-0.5 rounded bg-accent/10 text-accent text-[10px] font-semibold uppercase tracking-[.05em]">stopped itself</span>
                  <span className="text-[13px] font-semibold">{p.groupName} · {p.country} · {SOURCE_WORDS[p.source] ?? p.source}</span>
                </div>
                <p className="text-[12.5px] text-muted leading-relaxed">{p.reason}. Nothing else stopped.</p>
                <div className="flex gap-1.5">
                  <button
                    onClick={() => resume.mutate(p.id)} disabled={!d.mayEdit || resume.isPending}
                    className="px-2.5 py-1 rounded-lg border border-line-2 bg-page text-[11.5px] font-medium disabled:opacity-50"
                  >Start it again</button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 4 and 5. The loop reporting on itself, and what is going out. */}
      <div className="grid gap-4 lg:grid-cols-[1fr_330px] items-start">
        <section className="rounded-xl border border-line bg-panel p-3.5 flex flex-col gap-2.5">
          <div className="flex flex-wrap items-center gap-2.5">
            <span className={`w-[7px] h-[7px] rounded-full ${d.loop.running ? "bg-good" : "bg-line-2"}`} />
            <h2 className="text-[13px] font-semibold">{d.loop.running ? "The loop is running" : "The loop is off"}</h2>
            <button
              onClick={() => loop.mutate(!d.loop.running)}
              disabled={!d.mayEdit || loop.isPending || (!d.loop.running && d.missing.length > 0)}
              className="ml-auto px-2.5 py-1 rounded-lg border border-line-2 bg-page text-[11.5px] font-medium disabled:opacity-50"
            >{d.loop.running ? "Stop" : "Start it"}</button>
          </div>
          <p className="text-[12px] text-muted leading-relaxed">
            {d.loop.running
              ? `This week: ${d.loop.searches} ${d.loop.searches === 1 ? "search" : "searches"}, ${d.loop.found} companies found, ${d.loop.drafted} letters written.${d.loop.lastTickAt ? ` Last looked ${when(d.loop.lastTickAt)}.` : ""}`
              : "It finds, reads and writes on a schedule, and never decides who is a fit — your table does that."}
          </p>
          <div className="flex flex-wrap items-center gap-2.5 pt-0.5">
            <button
              onClick={() => setFinding(true)}
              className="px-2.5 py-1 rounded-lg border border-line-2 bg-page text-[11.5px] font-medium flex items-center gap-1.5"
            ><Icon name="search" size={12} /> Find companies</button>
            {searchPrice ? <span className="text-[11px] text-faint">a search by hand · {priceWords(searchPrice)}</span> : null}
          </div>
        </section>

        <section className="rounded-xl border border-line bg-panel p-3.5 flex flex-col gap-2">
          <h2 className="text-[13px] font-semibold">Going out</h2>
          {d.comingUp.length === 0
            ? <p className="text-[12.5px] text-faint">Nothing is scheduled.</p>
            : (
              <table className="w-full text-[12.5px]">
                <tbody>
                  {d.comingUp.map((r) => (
                    <tr key={r.id} className="border-t border-line first:border-0">
                      <td className="py-1 pr-2 truncate">{r.companyName}</td>
                      <td className="py-1 text-muted whitespace-nowrap">{due(r)}</td>
                      <td className="py-1 text-right">
                        <button
                          onClick={() => stop.mutate(r.prospectId)} disabled={stop.isPending}
                          className="text-[11px] text-muted hover:text-ink underline underline-offset-2 disabled:opacity-50"
                        >Stop</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          <p className="text-[11px] text-faint leading-relaxed">
            Nothing leaves outside your cap for the day, and never to anyone who asked you to stop.
          </p>
        </section>
      </div>

      {/* 6. One line of numbers, and what this box would let out. */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-line pt-3">
        {([["found", d.numbers.found], ["written to", d.numbers.letters], ["answered", d.numbers.replies], ["calls booked", d.numbers.meetings]] as const).map(([label, n]) => (
          <span key={label} className="flex items-baseline gap-1.5">
            <b className="text-[15px] font-bold tabular-nums">{n}</b>
            <span className="text-[11.5px] text-muted">{label}</span>
          </span>
        ))}
        <span className="flex items-baseline gap-1.5">
          {/* A dollar string with no sign, as every money figure crosses the wire (ADR 0002); the screen adds it. */}
          <b className="text-[15px] font-bold tabular-nums">${d.numbers.spentThisMonth}</b>
          <span className="text-[11.5px] text-muted">this month</span>
        </span>
        <span className={`ml-auto px-1.5 py-0.5 rounded text-[10.5px] font-semibold ${d.gate === "on" && d.sendingOn ? "bg-ink text-white" : "bg-line-2 text-ink-2"}`}>
          {gateWords(d)}
        </span>
      </div>

      {finding && <FindPanel onClose={() => setFinding(false)} />}
      {open && <CompanyPanel prospectId={open} onClose={() => setOpen(null)} onNext={(next) => setOpen(next)} />}
    </div>
  );
}

/** One letter in the queue: its company, what the judge made of it, and the one sentence written for it. */
function Waiting({ letter, onOpen }: { letter: ToApproveView; onOpen: () => void }) {
  const law = lettersGo(letter.country);
  const held = law.goes ? null : law.says;
  return (
    <button onClick={onOpen} className="w-full text-left flex gap-3 items-start px-3.5 py-2.5 border-b border-line last:border-0 hover:bg-page">
      <span className="flex flex-col gap-1 min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-[13px] font-semibold">{letter.companyName}</span>
          {letter.verdict && <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-page border border-line-2 text-muted">{letter.verdict} · {letter.score}</span>}
          {letter.groupName && <span className="text-[11.5px] text-muted">{letter.groupName}</span>}
          {letter.byLoop && <span className="text-[11px] text-faint">· the loop wrote this</span>}
        </span>
        {letter.opener && <span className="text-[12px] text-muted leading-relaxed">“{letter.opener}”</span>}
        {/* A draft written before its country was held: it can only be thrown away, and the row says why (ADR 0065). */}
        {held ? <span className="text-[11.5px] text-accent leading-relaxed">Will not go: {held}</span> : null}
      </span>
      <span className="text-[11.5px] font-medium text-ink shrink-0 mt-0.5">Read it</span>
    </button>
  );
}

/* ---------------------------------------------------------------------------------------------- the words ---- */

const KINDS: Record<ProposalView["kind"], string> = {
  reword: "different words", narrow: "a narrower group", retire: "retire it", try_market: "a market to try",
};

const headline = (p: ProposalView): string =>
  p.kind === "narrow" ? (p.name ?? "") : p.groupName ?? "";

const SOURCE_WORDS: Record<string, string> = {
  ask_ai: "Ask AI", map: "Google Places", sirene: "French register", companies_house: "Companies House",
};

const gateWords = (d: TodayResponse): string =>
  d.gate === "off" ? "nothing is sent from this box"
    : !d.mailboxProvider ? "no mailbox yet · Business → Customers connects one"
    : !d.sendingOn ? "sending is off · Business → Customers turns it on"
      : d.gate === "rehearsal" ? "rehearsing · every letter comes to you"
        : d.gate === "allowlist" ? "your own addresses only"
          : "live · letters reach companies";

function due(r: ComingUpView): string {
  const ms = new Date(r.dueAt).getTime() - Date.now();
  if (ms <= 0) return "on the next sweep";
  const hours = Math.round(ms / 3_600_000);
  if (hours < 24) return hours <= 1 ? "within the hour" : `in ${hours}h`;
  const days = Math.round(hours / 24);
  return days === 1 ? "tomorrow" : `in ${days} days`;
}

export function when(at: string): string {
  const ms = Date.now() - new Date(at).getTime();
  const minutes = Math.round(ms / 60_000);
  if (minutes < 2) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? "yesterday" : `${days} days ago`;
}
